import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tabs, Spin, Popover } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetch from 'utils/fetch/useFetch';
import get from 'lodash/get';
import unionBy from 'lodash/unionBy';
import { BOXES, WAREHOUSES, SECTION, DEVICES, USERS, MONITORING } from 'api';
import CellsTab from './components/CellsTab';
import ManagerTab from './components/ManagerTab';
import PopoverWrapper from '../Storages/components/PopoverWrapper';
import StorageForm from '../Storages/components/StorageForm';
import { ReactComponent as ArrowIcon } from '../../images/arrowback.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSuccess } from '../../store/reducers/fetchSlice';
import { useCreation } from '@umijs/hooks';
const { TabPane } = Tabs;

const TabsPanel = styled(Tabs)`
  &&& {
    .ant-tabs-tab {
      padding: 12px 10px;
      margin: 0;
      margin-top: 10px;
      outline: none !important;
    }
  }
`;

const WrapperTab = styled.div`
  &&& {
    .ant-tabs-nav {
      border-bottom: 1px solid #cacace;
    }
  }
`;

function StoragePage() {
  const [tab, setTab] = useState('cells');
  const [visible, setVisible] = useState(false);
  const handleTab = (key) => {
    setTab(key);
  };
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const logs = useSelector((state) =>
    get(state, `fetch.box_logs.dataSource`, null),
  );

  const { data: storage, getData, loading } = useFetch(WAREHOUSES, {
    uniqId: 'warehouse',
    autoFetch: false,
    entityId: id,
  });

  const { data: users, getData: getUsers, loading: loadingUsers, updateData:updateUsers } = useFetch(
    USERS,
    {
      uniqId: 'users',
      autoFetch: false,
    },
  );

  const {
    data: devices = [],
    getData: getDevices,
    updateData: updateDevice,
    loading: loadingDevice,
  } = useFetch(DEVICES, {
    uniqId: 'devices',
    autoFetch: false,
  });

  const { data: permissions = [], getData: getPermissions } = useFetch(
    `${WAREHOUSES}/${id}/user_permissions`,
    {
      uniqId: 'permissions',
      autoFetch: false,
    },
  );

  const {
    data: boxes = [],
    getData: getBoxes,
    postData: postBox,
    loading: boxLoading,
    updateData: updateBox,
  } = useFetch(BOXES, {
    uniqId: 'boxes',
    autoFetch: false,
  });

  const {
    data: section = [],
    getData: getSection,
    postData: postSection,
    updateData: updateSection,
    deleteData: deleteSection,
    loading: sectionLoading,
  } = useFetch(SECTION, {
    uniqId: 'section',
    autoFetch: false,
  });

  const { updateData } = useFetch(WAREHOUSES, {
    uniqId: 'warehouses',
    autoFetch: false,
  });

  const handleEdit = async (data) => {
    await updateData({ ...data });
    getData();
  };

  useEffect(() => {
    getData();
    getUsers({
      role: 'manager',
    });
    getBoxes({ warehouse_id: id });
    getSection({ warehouse_id: id });
  }, []);

  const webSocket = useCreation(() => {
    const token = localStorage.getItem('access_token');
    return new WebSocket(`${MONITORING}?authorization=OAuth%20${token}`);
  });

  useEffect(() => {
    webSocket.onopen = () => {
      console.log('open');
    };
    webSocket.onclose = () => console.log('ws closed');
    return () => {
      webSocket.close();
    };
  }, []);

  useEffect(() => {
    webSocket.onmessage = function (event) {
      const response = JSON.parse(event.data);
      if (boxes && response.type === 'box') {
        dispatch(
          fetchSuccess({
            uniqId: 'boxes',
            response: { data: unionBy([response.data], boxes, 'id') },
          }),
        );
      } else if (
        logs &&
        (response.type === 'box_log_insert' ||
          response.type === 'box_log_update')
      ) {
        let copy = logs.slice(0);
        copy.unshift(response.data);
        console.log(response.data)
        dispatch(
          fetchSuccess({
            uniqId: 'box_logs',
            response: { data: copy },
          }),
        );
      }
    };
  }, [dispatch, fetchSuccess, boxes, logs]);

  return (
    <Spin spinning={loading || boxLoading}>
      <div className="flex  items-center">
        <div className="flex items-center">
          <Link to="/">
            <ArrowIcon />
          </Link>
        </div>
        <div className="pl-10 text-black text-18 font-bold">
          {storage ? storage.name : null}
        </div>
        <PopoverWrapper>
          <Popover
            content={
              <StorageForm
                setVisible={setVisible}
                open={visible}
                data={storage}
                edit={handleEdit}
              />
            }
            className="rounded-lg"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            trigger="click"
            visible={visible}
            onVisibleChange={() => {
              setVisible(!visible);
            }}
          >
            <Button
              onClick={() => {
                setVisible(!visible);
              }}
              type="primary"
              className="pl-18"
              type="link"
            >
              <span style={{ borderBottom: '1px dotted #4b97e9' }}>
                {t('more')}
              </span>
            </Button>
          </Popover>
        </PopoverWrapper>
      </div>
      <WrapperTab>
        <TabsPanel defaultActiveKey="cells" onChange={handleTab}>
          <TabPane className="outline-none" tab={t('cells')} key="cells" />
          <TabPane className="p0" tab={t('managers')} key="managers" />
        </TabsPanel>
      </WrapperTab>
      {tab === 'cells' ? (
        <CellsTab
          locks={devices}
          deleteSection={deleteSection}
          updateSection={updateSection}
          storage={storage}
          postSection={postSection}
          getSection={getSection}
          getBoxes={getBoxes}
          boxes={boxes}
          section={section}
          postBox={postBox}
          getSection={getSection}
          getDevices={getDevices}
          updateDevice={updateDevice}
          updateBox={updateBox}
          getUsers={getUsers}
          users={users}
          loadingDevice={loadingDevice}
          updateUsers={updateUsers}
        />
      ) : (
        <ManagerTab
          loadingUsers={loadingUsers}
          users={users}
          getUsers={getUsers}
          getPermissions={getPermissions}
          permissions={permissions}
        />
      )}
    </Spin>
  );
}

StoragePage.propTypes = {};

export default StoragePage;
